// Parallax
// Features:
// - Subtle camera sway based on mouse position in the menu
// - Smooth interpolation and clamping of movement
// - Active only during 'menuIdle' phase

import { useRef, useEffect } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'

import { usePhase, PHASES } from '../../store/usePhase'
import { useStore } from '../../store/useStore'

export default function Parallax({
    positionIntensity = 0.7,
    damping = 2.0,
    zFactor = 0.5,
    controlsRef,
}) {
    const camera = useThree((state) => state.camera)
    const phase = usePhase((state) => state.phase)
    const parallaxEnabled = useStore((state) => state.parallaxEnabled)

    const basePosRef = useRef(new THREE.Vector3())
    const hasBaseRef = useRef(false)
    const offset = useRef({ x: 0, y: 0 })
    const target = useRef({ x: 0, y: 0 })

    // Capture base camera position when entering menu
    useEffect(() => {
        if (phase === PHASES.menuIdle && camera) {
            basePosRef.current.copy(camera.position)
            hasBaseRef.current = true
            offset.current.x = 0
            offset.current.y = 0
        } else {
            hasBaseRef.current = false
        }
    }, [phase, camera])

    useFrame((state, delta) => {
        if (phase !== PHASES.menuIdle || !parallaxEnabled || !hasBaseRef.current) return

        // Re-capture base if camera has drifted significantly (safety check)
        if (camera.position.distanceTo(basePosRef.current) > 50) {
            basePosRef.current.copy(camera.position)
            offset.current = { x: 0, y: 0 }
        }

        // Update target from pointer
        target.current.x = state.pointer.x * -1
        target.current.y = state.pointer.y * -1

        // Lerp offset
        offset.current.x = THREE.MathUtils.lerp(
            offset.current.x,
            target.current.x * positionIntensity,
            delta * damping
        )
        offset.current.y = THREE.MathUtils.lerp(
            offset.current.y,
            target.current.y * positionIntensity,
            delta * damping
        )

        // Clamp and Apply
        const clampedX = THREE.MathUtils.clamp(offset.current.x, -1.25, 1.25)
        const clampedY = THREE.MathUtils.clamp(offset.current.y, -1.25, 1.25)

        camera.position.set(
            basePosRef.current.x + clampedX,
            basePosRef.current.y - clampedY,
            basePosRef.current.z + clampedX * zFactor
        )

        if (controlsRef?.current) {
            controlsRef.current.update()
        }
    })

    return null
}
